<style lang="scss">
  @use "../../../../assets/css/design-system/utils";
  @use "../../../../assets/css/design-system/colors";

  label {
    all: unset;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    .padding {
      padding-bottom: utils.in_rem(12px);
    }

    .inner {
      overflow-y: auto;
      &.restrict-height {
        max-height: utils.in_rem(200px);
      }
    }
  }

  .required {
    @include colors.color(systemError_100);
  }
</style>

<script>
  import Search from "../../../atoms/Search/Search.svelte";
  import Display from "../Display.svelte";
  import Dropdown from "../Dropdown.svelte";
  import ListItem from "../ListItem.svelte";
  import NoItems from "../NoItems.svelte";

  import HelperText from "../../../atoms/InputLabels/HelperText.svelte";
  import Label from "../../../atoms/InputLabels/Label.svelte";

  let cls = "";
  export { cls as class };

  // Props for the label above the dropdown
  export let label = "";
  export let labelSize = "small";
  export let tooltipMessage = "";

  // Props for the helper text below the dropdown
  export let helperText = "";
  export let state = "";

  export let leadingIcon = ""; // Icon to display on the left of the dropdown
  export let placeholder = "Select"; // Text to display when no item is selected
  export let placeholderIcon = ""; // Icon to display when no items are selected
  export let items = []; // All options to choose from (use id, label, and icon)
  export let selected; // Id of the selected option (you can bind to this)
  export let style = "";
  export let showFilter = false; // Search bar at top of dropdown
  export let showDescription = false; // Whether to show descriptions for items
  export let restrictHeight = false; // Whether to restrict height of dropdown
  export let listDescriptionStyle = ""; // Style for the description of the list items
  export let scrollToSelected = true; // Whether to scroll to selected item when dropdown opens
  export let dropdownSize = "large"; // Size of the dropdown

  // Filter items based on search query
  let searchQuery = "";
  $: filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  let focusSearch;
  const dropdownClick = () => {
    if (focusSearch) focusSearch();

    if (scrollToSelected) {
      const selectedItemElement = document.querySelector(
        ".inner [aria-selected=true]"
      );
      if (selectedItemElement) {
        selectedItemElement.scrollIntoView({
          behavior: "auto",
          block: "center",
        });
      }
    }
    searchQuery = "";
  };

  // Update Display component variables based on selected item
  let displayText = "";
  let displayIcon = "";
  let displayTextDescription = "";
  let displayTextRequired = false;
  $: {
    const selectedItem = items.find((item) => item.id === selected);
    displayText = selectedItem?.label || placeholder;
    displayIcon = selectedItem?.icon || placeholderIcon;
    displayTextDescription = showDescription
      ? selectedItem?.description || ""
      : "";
    displayTextRequired = selectedItem?.required || false;
  }

  let closeDropdown;

  const dropdownProps = {
    style: style || "width: 100%",
    leadingIcon,
    kind: "input-field",
    size: dropdownSize,
    onclick: dropdownClick,
  };
</script>

<label class={cls} for="dropdown">
  {#if label}
    <Label {label} {labelSize} {tooltipMessage} />
  {/if}

  <Dropdown {...dropdownProps} bind:close={closeDropdown} {...$$restProps}>
    <Display
      slot="button"
      {displayIcon}
      {displayText}
      {displayTextDescription}
      {displayTextRequired}
    />

    <span slot="header" class:padding={showFilter}>
      {#if showFilter}
        <Search
          bind:value={searchQuery}
          bind:focus={focusSearch}
          class="flex-0-0"
          style="z-index: 1"
        />
      {/if}
    </span>

    <div class="inner" class:restrict-height={restrictHeight}>
      {#each filteredItems as item}
        <ListItem
          icon={item.icon}
          src={item.src}
          selected={selected === item.id}
          onclick={() => {
            selected = item.id;
            closeDropdown();
          }}
          description={showDescription && item.description
            ? item.description
            : ""}
          descriptionStyle={listDescriptionStyle}
        >
          {item.label}
          {#if item.required}
            <span class="required">*</span>
          {/if}
        </ListItem>
      {/each}
    </div>

    <!-- No items found from filter -->
    {#if filteredItems.length === 0}
      <NoItems />
    {/if}
  </Dropdown>

  {#if helperText}
    <HelperText {helperText} {state} />
  {/if}
</label>
